
import { Injectable, OnDestroy, inject } from "@angular/core";
import _ from "lodash";
import { Observable, ReplaySubject, Subject, of } from "rxjs";
import { switchMap, takeUntil } from "rxjs/operators";
import { ApiClientService } from "~app/common/services/api-client.service";
import { AddAnmalanTradslagCommand } from "../commands/atgard-mbf/AddAnmalanTradslagCommand";
import { DeleteAnmalanTradslagCommand } from "../commands/atgard-mbf/DeleteAnmalanTradslagCommand";
import { UpdateAnmalanTradslagCommand } from "../commands/atgard-mbf/UpdateAnmalanTradslagCommand";
import { UpdateForyngringsMetodCommand } from "../commands/atgard-mbf/UpdateForyngringsMetodCommand";
import { UpdateMarkberedningsMetodCommand } from "../commands/atgard-mbf/UpdateMarkberedningsMetodCommand";
import { AnmalanTradslag, TradslagDTO, VKAtgardMbf, VKAtgardMbfDTO } from "../models/mbf-models";
import { AtgardMediatorService } from "./atgard-mediator.service";
import { KontraktCommandService } from "./kontrakt-command.service";

@Injectable()
export class AtgardMbfService implements OnDestroy {
    readonly anmalanTradslagChanged$ = new Subject<void>();

    private readonly kontraktCommandService = inject(KontraktCommandService);
    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly api = inject(ApiClientService);

    private readonly atgardMbfSrc = new ReplaySubject<VKAtgardMbf>(1);
    private readonly destroy$ = new Subject<void>();
    get atgardMbf$() {
        return this.atgardMbfSrc.asObservable();
    }

    constructor() {
        this.atgardMediatorService.atgard$
            .pipe(
                switchMap(atgard => this.getMbfForAtgard(atgard.id)),
                takeUntil(this.destroy$)
            )
            .subscribe(mbf => this.atgardMbfSrc.next(mbf));
    }
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getMbfForAtgard(atgardId?: number ): Observable<VKAtgardMbfDTO> {
        if (atgardId) {
            return this.api.get<VKAtgardMbfDTO>(`virkeskop2/atgard/${atgardId}/atgardMbf`);
        } else { return of(new VKAtgardMbfDTO());}
    }

    getMarkberedningsMetoder(): Observable<string[]> {
        return this.api.get<string[]>("virkeskopsatgard/basdata/markberedningsmetoder", false);
    }

    getForyngringsmetoder(): Observable<string[]> {
        return this.api.get<string[]>("virkeskopsatgard/basdata/foryngringsmetoder", false);
    }

    updateForyngringsMetod(virkeskopsatgardGuid: string, metod: string) {
        const cmd = new UpdateForyngringsMetodCommand(virkeskopsatgardGuid, metod);
        this.kontraktCommandService.update(cmd);
    }

    getTradslag(): Observable<TradslagDTO[]> {
        return this.api.get<TradslagDTO[]>("tradslagsdata", false);
    }

    updateMarkberedningsMetod(virkeskopsatgardGuid: string, metod: string) {
        const cmd = new UpdateMarkberedningsMetodCommand(virkeskopsatgardGuid, metod);
        this.kontraktCommandService.update(cmd);
    }

    addAnmalanTradslag(anmalantradslagGuid: string) {
        this.anmalanTradslagChanged$.next();
        const atgardGuid = this.atgardMediatorService.atgard.guid;
        const cmd = new AddAnmalanTradslagCommand(atgardGuid, anmalantradslagGuid);
        this.kontraktCommandService.update(cmd);
    }

    updateAnmalanTradslag(anmalantradslagGuid: string, tradslagId: number, arealHa: number, antalPlantor: number) {
        this.anmalanTradslagChanged$.next();
        const atgardGuid = this.atgardMediatorService.atgard.guid;
        const cmd = new UpdateAnmalanTradslagCommand(atgardGuid, anmalantradslagGuid, tradslagId, arealHa, antalPlantor);
        this.kontraktCommandService.update(cmd);
    }

    deleteAnmalanTradslag(anmalantradslagGuid: string) {
        this.anmalanTradslagChanged$.next();
        const atgardGuid = this.atgardMediatorService.atgard.guid;
        const cmd = new DeleteAnmalanTradslagCommand(atgardGuid, anmalantradslagGuid);
        this.kontraktCommandService.update(cmd);
    }

    getTotalArea(anmalanTradslag: AnmalanTradslag[]): number {
        return _.round(_.sumBy(anmalanTradslag, i => i.arealHa), 1);
    }

}
